// Static content for the Kairos Buildings marketing site
import {
  Home,
  Ruler,
  PaintRoller,
  Building2,
  Wrench,
} from "lucide-react";

export const BRAND = {
  name: "Kairos Buildings",
  tagline: "Create & build together",
  phone: "(250) 231-2645",
  email: "info@kairosbuildings.ca",
  address: "Serving the West Kootenays — Trail to Nelson & surrounding areas",
  serviceAreas: ["West Kootenays", "Trail", "Nelson", "& surrounding areas"],
  logoLight:
    "https://customer-assets.emergentagent.com/job_construct-hub-456/artifacts/5otcmmyo_Kairos%20Buildings-01-2.jpg",
  logoDark:
    "https://customer-assets.emergentagent.com/job_construct-hub-456/artifacts/8yxbr66i_Kairos%20Buildings-02-2.jpg",
};

export const IMAGES = {
  hero: "https://images.unsplash.com/photo-1628744448840-55bdb2497bd4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjByZXNpZGVudGlhbCUyMGhvbWUlMjBleHRlcmlvcnxlbnwwfHx8fDE3ODE2NTgyMDZ8MA&ixlib=rb-4.1.0&q=85",
  portfolio1:
    "https://images.unsplash.com/photo-1613490493576-7fde63acd811?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjByZXNpZGVudGlhbCUyMGhvbWUlMjBleHRlcmlvcnxlbnwwfHx8fDE3ODE2NTgyMDZ8MA&ixlib=rb-4.1.0&q=85",
  portfolio2:
    "https://images.unsplash.com/photo-1714860534425-7ce04e013dec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBraXRjaGVuJTIwcmVtb2RlbGluZyUyMGludGVyaW9yfGVufDB8fHx8MTc4MTY1ODIwNnww&ixlib=rb-4.1.0&q=85",
  portfolio3:
    "https://images.unsplash.com/photo-1628012209120-d9db7abf7eab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjByZXNpZGVudGlhbCUyMGhvbWUlMjBleHRlcmlvcnxlbnwwfHx8fDE3ODE2NTgyMDZ8MA&ixlib=rb-4.1.0&q=85",
  about:
    "https://images.unsplash.com/photo-1587582423116-ec07293f0395?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjB3b3JrZXJzJTIwYnVpbGRpbmclMjBob3VzZXxlbnwwfHx8fDE3ODE2NTgyMDZ8MA&ixlib=rb-4.1.0&q=85",
};

// Ground-up custom home — a single project told as a 5-stage progression
export const HOME_BUILD = {
  eyebrow: "Ground-Up Custom Home",
  title: "Bare foundation to finished home",
  description:
    "One of our ground-up builds, start to finish. Follow it from a waterproofed foundation through framing, sheathing and weather envelope to a finished, move-in-ready exterior — every stage handled by one accountable crew.",
  steps: [
    { no: "01", src: "/projects/home-1-foundation.jpg", label: "Foundation & subfloor" },
    { no: "02", src: "/projects/home-2-framing.jpg", label: "Framing" },
    { no: "03", src: "/projects/home-3-sheathing.jpg", label: "Sheathing & roof" },
    { no: "04", src: "/projects/home-4-wrap.jpg", label: "Weather envelope" },
    { no: "05", src: "/projects/home-5-finished.jpg", label: "Finished exterior" },
  ],
};

// Before / after storytelling blocks
export const PROJECTS = [
  {
    id: "lakeside-roof",
    category: "Roof Replacement",
    title: "Lakeside Roof Replacement",
    description:
      "This lakefront home's aging cedar shakes were losing the fight against West Kootenays weather. We tore off, re-decked where needed and laid a crisp new architectural shingle roof — protecting the view for decades to come.",
    before: "/projects/roof-before.jpg",
    after: "/projects/roof-after.jpg",
    beforeLabel: "Before",
    afterLabel: "After",
  },
  {
    id: "grey-oak-staircase",
    category: "Staircase Renovation",
    title: "Grey-Oak Staircase Transformation",
    description:
      "We stripped a dated staircase back to bare structure and rebuilt it into a warm grey-oak showpiece — custom black steel spindles, solid wood posts and crisp, level treads throughout.",
    before: "/projects/stair-grey-before.jpg",
    after: "/projects/stair-grey-after.jpg",
    beforeLabel: "Before",
    afterLabel: "After",
  },
  {
    id: "covered-deck",
    category: "Decks & Outdoor Living",
    title: "Covered Deck & Pergola Rebuild",
    description:
      "A weathered deck on a failing support was rebuilt from the posts up — then crowned with a custom gabled pergola and glass railing, framed to capture the mountain view rain or shine.",
    before: "/projects/deck-before.jpg",
    after: "/projects/deck-finished.jpg",
    beforeLabel: "Before",
    afterLabel: "After",
  },
];

// "More of our work" — masonry gallery
export const GALLERY = [
  { src: "/projects/gallery-whiteoak-stair.jpg", caption: "White-oak feature staircase", tall: true },
  { src: "/projects/gallery-lakeside-deck.jpg", caption: "Lakeside composite deck", tall: false },
  { src: "/projects/gallery-pavilion.jpg", caption: "Timber-frame pavilion", tall: false },
  { src: "/projects/gallery-interior-remodel.jpg", caption: "Open-concept interior remodel", tall: false },
  { src: "/projects/deck-framing.jpg", caption: "Deck & pergola framing", tall: false },
  { src: "/projects/gallery-covered-patio.jpg", caption: "Covered patio addition", tall: false },
  { src: "/projects/gallery-deck-2.jpg", caption: "Covered deck, finished", tall: true },
  { src: "/projects/gallery-partition-framing.jpg", caption: "Interior partition framing", tall: false },
  { src: "/projects/gallery-cedar-living.jpg", caption: "Cedar-panelled living space", tall: false },
];

// Numbered manifesto chapters
export const MANIFESTO = [
  {
    no: "01",
    title: "Every project begins with vision.",
    body: "Before a single nail is driven, we listen. Your vision, your budget, your timeline — it all starts as a conversation, not a contract.",
  },
  {
    no: "02",
    title: "Every vision needs a plan.",
    body: "We believe every dream is won in the planning stage. Educated planning, every need considered, quality held in mind for each step of the way.",
  },
  {
    no: "03",
    title: "We build it together.",
    body: "One dedicated crew, transparent pricing and clear weekly updates. You are never lost in the process — you are part of it.",
  },
  {
    no: "04",
    title: "Built to last a lifetime.",
    body: "Premium materials, skilled trades and an obsessive eye for detail. We hand back a home that outlives the build — and earns the referral.",
  },
];

export const MARQUEE = [
  "Create & build together",
  "Custom Homes",
  "Whole-Home Remodels",
  "Decks & Outdoor Living",
  "West Kootenays",
  "Licensed & Insured",
];

export const SERVICES = [
  {
    icon: Home,
    title: "Custom Home Building",
    desc: "Ground-up residential construction tailored to how you live — from foundation to final walkthrough.",
  },
  {
    icon: PaintRoller,
    title: "Whole-Home Remodeling",
    desc: "Reimagine your space with full-scale renovations that modernize layout, light, and flow.",
  },
  {
    icon: Building2,
    title: "Additions & Extensions",
    desc: "Add square footage seamlessly — second stories, in-law suites, sunrooms and beyond.",
  },
  {
    icon: Ruler,
    title: "Design & Planning",
    desc: "Collaborative design-build planning, permitting and carefully scheduling each trade before a nail is driven.",
  },
  {
    icon: Wrench,
    title: "Maintenance & Repairs",
    desc: "Periodic maintenance that suits your needs — from residential to commercial properties and everything in between.",
  },
];

export const SERVICE_OPTIONS = [
  "Custom Home Building",
  "Whole-Home Remodeling",
  "Additions & Extensions",
  "Design & Planning",
  "Maintenance & Repairs",
];

export const PROCESS = [
  { step: "01", title: "Consult", desc: "We listen to your vision, budget and timeline." },
  { step: "02", title: "Design", desc: "Detailed plans, 3D renders and transparent estimates." },
  { step: "03", title: "Build", desc: "Skilled crews, clear communication, zero surprises." },
  { step: "04", title: "Handover", desc: "A final walkthrough and a home built to last." },
];

export const STATS = [
  { value: "18+", label: "Years Building" },
  { value: "150+", label: "Projects Delivered" },
  { value: "98%", label: "On-Time Completion" },
  { value: "100%", label: "Licensed & Insured" },
];

export const TESTIMONIALS = [
  {
    name: "Marcus & Elena Reyes",
    project: "Custom Home, Hillcrest",
    quote:
      "Kairos turned our sketches into the home we'll grow old in. Every milestone was on time and the craftsmanship is flawless.",
  },
  {
    name: "Priya Natarajan",
    project: "Kitchen & Bath Remodel",
    quote:
      "The team communicated daily and respected our budget. Our kitchen went from dated to magazine-worthy in eight weeks.",
  },
  {
    name: "The Donnelly Family",
    project: "Second-Story Addition",
    quote:
      "They added an entire floor without us ever feeling lost in the process. Genuinely a partner — not just a contractor.",
  },
];
